import Lottie, { type LottieRefCurrentProps } from 'lottie-react';
import { useReducedMotion } from 'motion/react';
import { forwardRef, memo, useCallback, useImperativeHandle, useRef } from 'react';

import { cn } from '@air/utils-tailwind';

import {
  WORKSPACE_AGENT_AIR_MARK_ANIMATION_DATA,
  WORKSPACE_AGENT_AIR_MARK_DRAWN_FRAME,
  WORKSPACE_AGENT_AIR_MARK_RENDERER_SETTINGS,
} from '~/components/v2/components/workspace/workspace-agent-panel/components/entry/workspaceAgentAirMarkConstants';

export type WorkspaceAgentAirMarkHandle = {
  replay: () => void;
};

export type WorkspaceAgentAirMarkProps = {
  /** Play the burst on mount and rest on the drawn "A" (e.g. Start task and edit-tags promo CTAs). */
  autoplayOnMount?: boolean;
  className?: string;
  /** Burst strokes are white; invert on light menu panels. Agent bar rows stay dark. */
  surfaceTheme?: 'dark' | 'light';
};

/**
 * Shared agent entry wordmark — Strato `AgentRolloverBurst` (menus, agentic-blue CTAs, promos).
 */
export const WorkspaceAgentAirMark = memo(
  forwardRef<WorkspaceAgentAirMarkHandle, WorkspaceAgentAirMarkProps>(function WorkspaceAgentAirMark(
    { autoplayOnMount = false, className = 'size-4 shrink-0', surfaceTheme = 'dark' },
    ref,
  ) {
    const shouldReduceMotion = !!useReducedMotion();
    const markRef = useRef<LottieRefCurrentProps>(null);

    const showDrawnFrame = useCallback(() => {
      markRef.current?.goToAndStop(WORKSPACE_AGENT_AIR_MARK_DRAWN_FRAME, true);
    }, []);

    useImperativeHandle(ref, () => ({
      replay() {
        if (shouldReduceMotion) {
          return;
        }

        markRef.current?.goToAndPlay(0, true);
      },
    }));

    const handleMarkDomLoaded = useCallback(() => {
      if (shouldReduceMotion) {
        showDrawnFrame();
        return;
      }

      if (!autoplayOnMount) {
        markRef.current?.goToAndStop(0, true);
      }
    }, [autoplayOnMount, shouldReduceMotion, showDrawnFrame]);

    const handleComplete = useCallback(() => {
      showDrawnFrame();
    }, [showDrawnFrame]);

    return (
      <Lottie
        animationData={WORKSPACE_AGENT_AIR_MARK_ANIMATION_DATA}
        aria-hidden
        autoplay={autoplayOnMount && !shouldReduceMotion}
        className={cn('pointer-events-none', surfaceTheme === 'light' && 'invert', className)}
        loop={false}
        lottieRef={markRef}
        onComplete={handleComplete}
        onDOMLoaded={handleMarkDomLoaded}
        rendererSettings={WORKSPACE_AGENT_AIR_MARK_RENDERER_SETTINGS}
      />
    );
  }),
);

WorkspaceAgentAirMark.displayName = 'WorkspaceAgentAirMark';
